<template>
    <g v-if="item" class="map-location-marker" :class="[variant, { 'different-floor': !isSameFloor }]" :transform="markerTransform">
        <defs v-if="showVisionCone">
            <radialGradient :id="visionConeGradientId" cx="50%" cy="100%" r="100%" fx="50%" fy="100%">
                <stop offset="0%" stop-color="#4285F4" stop-opacity="1" />
                <stop offset="32%" stop-color="#4285F4" stop-opacity="1" />
                <stop offset="60%" stop-color="#4285F4" stop-opacity="0.3" />
                <stop offset="100%" stop-color="#4285F4" stop-opacity="0" />
            </radialGradient>
        </defs>

        <circle v-if="showGpsAccuracyCircle" class="gps-accuracy-circle" :r="gpsAccuracyCircleRadius" />

        <g class="fixed-px-size">
            <template v-if="outerRadius">
                <circle @mousedown.self="$emit('selected', item)" class="circle-around" stroke="3" r="40" />
<!--                <g v-if="showOuterOrientation" :transform="outerOrientationTransform">-->
<!--                    <path class="circle-orientation" d="M -0.1 0 L -0.5 1 Q 0 1.3 0.5 1 L 0.1 0 Z" style="opacity: 1" />-->
<!--                </g>-->
            </template>

            <circle v-if="shape === 'circle' && currentMap.style.icons.theme !== 'glyph'"
                @mousedown.self="$emit('selected', item)" class="circle-marker" stroke="3" r="10" />
            <template v-else-if="shape === 'dot'">
                <g v-if="showVisionCone" ref="visionConeGroup" :transform="visionConeTransform">
                    <path class="vision-cone-path" :d="visionConePath" :fill="visionConeFill" />
                </g>

                <g v-if="showVisionCone" ref="personArrowGroup" :transform="personArrowTransform">
                    <g :transform="dotArrowScaleTransform" style="pointer-events: none;">
                        <path class="arrow-marker" d="M -20 0 L 0 20 L 20 0 L 0 0 Z" />
                    </g>
                </g>
                <circle class="blue-dot-inner" fill="url(#blue-dot-inner-gradient)" r="12" />
            </template>
            <template v-else-if="shape === 'arrow'">
                <g ref="arrowGroup" :transform="arrowTransform">
                    <path class="arrow-marker" d="M -0.6 -0.5 L 0 1 L 0.6 -0.5 L 0 -0.25 Z"
                        style="stroke: white; stroke-width: 0.1px; stroke-linejoin: round;" />
                </g>
            </template>
            <template v-else-if="shape === 'person'">
                <g v-if="showVisionCone" ref="visionConeGroup" :transform="visionConeTransform">
                    <path class="vision-cone-path" :d="visionConePath" :fill="visionConeFill" />
                </g>

                <g v-if="showVisionCone" ref="personArrowGroup" :transform="personArrowTransform">
                    <g :transform="personArrowScaleTransform" style="pointer-events: none;">
                        <path class="arrow-marker" d="M -20 0 L 0 20 L 20 0 L 0 0 Z" />
                    </g>
                </g>

                <circle class="blue-dot-inner" fill="url(#blue-dot-inner-gradient)" r="16" />
                <image class="walking-person" :href="walkingPersonUrl" x="-12" y="-12" width="24" height="24" />
            </template>
            <template v-else-if="shape === 'binoculars-tooltip'">
                <g class="binoculars-tooltip-marker">
                    <!-- <path class="binoculars-tooltip-bg" d="M -18 -18 L 18 -18 Q 28 -18 28 -8 L 28 14 Q 28 24 18 24 L 8 24 L 0 32 L -8 24 L -18 24 Q -28 24 -28 14 L -28 -8 Q -28 -18 -18 -18 Z" /> -->
                    <!-- <text class="binoculars-tooltip-icon" y="6" dominant-baseline="middle" text-anchor="middle">&#xf06e;</text> -->
                    <circle class="binoculars-tooltip-circle" r="18" />
                    <svg class="binoculars-tooltip-icon" xmlns="http://www.w3.org/2000/svg" x="-15" y="-17" width="30" height="30" viewBox="0 0 24 24"><!-- Icon from Tabler Icons by Paweł Kuna - https://github.com/tabler/tabler-icons/blob/master/LICENSE --><path fill="currentColor" d="M8.887 6.748c-.163 0-.337.016-.506.057c-.172.041-.582.165-.838.562l-.014.02l-.607 1.05c-.307.205-.534.46-.693.717l-.014.02l-2.572 4.65a4 4 0 0 0-.274.494l-.006.01A4 4 0 0 0 3 16a4 4 0 0 0 8 0v-1h2v1a4 4 0 1 0 7.635-1.67l-.004-.012a4 4 0 0 0-.274-.494l-2.572-4.65l-.014-.02a2.3 2.3 0 0 0-.693-.716l-.607-1.051l-.014-.02c-.256-.397-.667-.52-.838-.562a2.2 2.2 0 0 0-.664-.051a2 2 0 0 0-.68.156c-.184.081-.638.327-.754.889l-.007.037l-.14 1.1c-.22.283-.374.64-.374 1.064v1h-2v-1c0-.424-.154-.781-.373-1.064l-.14-1.1l-.008-.037c-.116-.562-.57-.808-.754-.889a2 2 0 0 0-.68-.156a2 2 0 0 0-.158-.006M7 14a2 2 0 1 1-1.838 1.209l.19-.342C5.712 14.344 6.316 14 7 14m10 0c.684 0 1.288.344 1.648.867l.19.342A2 2 0 1 1 17 14"/></svg>
                </g>
            </template>

            <template v-if="label">
                <rect class="location-marker-bg"  rx="8" ry="8" fill="#eee" style="opacity: 0.85" />
                <text class="location-marker-label" dominant-baseline="middle" text-anchor="middle">
                    {{ label }}
                </text>
            </template>
        </g>
    </g>
</template>

<script>
import MapMixin from "@/helpers/maps/map-mixin.js";
import walkingPersonUrl from '@/assets/icons/wayfinding/walking-person-filled.gif';

const VISION_CONE_PATHS = {
    25: `M 0 0 L -21.1 -45.3 L 21.1 -45.3 Z`,
    40: `M 0 0 L -32.1 -38.3 L 32.1 -38.3 Z`,
    55: `M 0 0 L -41.0 -28.7 L 41.0 -28.7 Z`,
    70: `M 0 0 L -47.0 -17.1 L 47.0 -17.1 Z`
};

let markerGradientSequence = 0;

export default {
    name: `map-location-marker`,
    mixins: [MapMixin],
    props: {
        item: { type: Object, required: true },
        variant: { type: String, default: `secondary` },
        angle: { type: [String, Number] },
        accuracy: { type: Number, default: 45 }, // Accuracy in degrees (lower = more accurate)
        gpsAccuracy: { type: Number, default: null }, // GPS accuracy in meters (lower = more accurate, controls the map-scaled blue circle radius)
        shape: { type: String, default: `circle` }, // circle | dot | arrow | person | binoculars-tooltip
        outerRadius: { type: Boolean },
        floor: { type: Object, required: true },
        label: { type: String },
        noArrow: { type: Boolean, default: false },
        liveCompass: {
            validator: (value) => value === null || value instanceof EventTarget,
            default: null
        }
    },
    data() {
        return {
            walkingPersonUrl,
            visionConeGradientId: ``,
            liveAccuracy: null,
            isCompassActive: false,
            animatedGpsAccuracyMeters: 0,
            gpsAccuracyAnimationFrame: null
        }
    },
    created() {
        markerGradientSequence += 1;
        this.visionConeGradientId = `vision-cone-gradient-${markerGradientSequence}`;
        this.isCompassActive = false;
        this.animatedGpsAccuracyMeters = this.normalizeGpsAccuracy(this.gpsAccuracy) ?? 0;
    },
    mounted() {
        this.startLiveCompass();
    },
    beforeDestroy() {
        this.stopLiveCompass();
        this.stopGpsAccuracyRadiusAnimation();
    },
    beforeUnmount() {
        this.stopLiveCompass();
        this.stopGpsAccuracyRadiusAnimation();
    },
    computed: {
        showGpsAccuracyCircle() {
            return (this.shape === `person` || this.shape === `dot`) && this.animatedGpsAccuracyMeters > 0;
        },
        gpsAccuracyCircleRadius() {
            return this.animatedGpsAccuracyMeters > 0 ? this.animatedGpsAccuracyMeters : 0;
        },
        effectiveAngle() {
            return this.angle;
        },
        effectiveAccuracy() {
            if (this.useLiveCompass && this.liveAccuracy !== null) {
                return this.liveAccuracy;
            }
            return this.accuracy;
        },
        normalizedAngle() {
            const value = Number(this.effectiveAngle);
            return Number.isFinite(value) ? value : 0;
        },
        isSameFloor() {
            return this.item.floorId === this.floor.id;
        },
        markerTransform() {
            return `translate(${this.item.x} ${this.item.y})`;
        },
        // showOuterOrientation() {
        //     const value = Number(this.item?.angle);
        //     return Number.isFinite(value) && value !== 0;
        // },
        // outerOrientationTransform() {
        //     return `translate(0 0) rotate(${Number(this.item.angle) - 180}) scale(35)`;
        // },
        arrowTransform() {
            return `translate(0 0) rotate(${this.normalizedAngle - 180}) scale(27)`;
        },
        showVisionCone() {
            return (this.shape === `person` || this.shape === `dot`) && !this.noArrow;
        },
        visionConeTransform() {
            return `rotate(${this.normalizedAngle})`;
        },
        personArrowTransform() {
            return `rotate(${this.normalizedAngle - 180})`;
        },
        personArrowScaleTransform() {
            return `translate(0 10) scale(0.7)`;
        },
        dotArrowScaleTransform() {
            return `translate(0 8) scale(0.5)`;
        },
        visionConeFill() {
            return `url(#${this.visionConeGradientId})`;
        },
        /**
         * Vision cone half-angle based on accuracy (like Google Maps)
         * Low accuracy (high value) = wider cone
         * High accuracy (low value) = narrower cone
         *
         * Accuracy ranges:
         * - Very accurate (< 15°): narrow cone (15°)
         * - Good (15-30°): medium-narrow cone (25°)
         * - Moderate (30-50°): medium cone (40°)
         * - Poor (50-70°): wide cone (55°)
         * - Very poor (> 70°): very wide cone (70°)
         */
        visionConeAngle() {
            const acc = this.effectiveAccuracy;
            if (acc < 15) {
                return 25;
            }
            if (acc < 50) {
                return 40;
            }
            if (acc < 70) {
                return 55;
            }
            return 70;
        },
        visionConePath() {
            return VISION_CONE_PATHS[this.visionConeAngle] || VISION_CONE_PATHS[70];
        },
        useLiveCompass() {
            return this.liveCompass && (this.shape === `arrow` || this.shape === `person` || this.shape === `dot`);
        }
    },
    methods: {
        normalizeGpsAccuracy(value) {
            const accuracy = Number(value);
            if (!Number.isFinite(accuracy) || accuracy <= 0) {
                return null;
            }
            return Math.max(0, accuracy);
        },
        animateGpsAccuracyRadius(value) {
            const normalizedAccuracy = this.normalizeGpsAccuracy(value);
            const targetMeters = normalizedAccuracy ?? 0;
            const currentMeters = Number.isFinite(this.animatedGpsAccuracyMeters)
                ? this.animatedGpsAccuracyMeters
                : targetMeters;

            if (Math.abs(targetMeters - currentMeters) < 0.05) {
                this.animatedGpsAccuracyMeters = targetMeters;
                return;
            }

            if (typeof requestAnimationFrame !== `function`) {
                this.animatedGpsAccuracyMeters = targetMeters;
                return;
            }

            this.stopGpsAccuracyRadiusAnimation();

            const duration = Math.max(220, Math.min(900, 240 + Math.abs(targetMeters - currentMeters) * 12));
            const startTime = typeof performance !== `undefined` ? performance.now() : Date.now();
            const step = (timestamp) => {
                const now = typeof timestamp === `number`
                    ? timestamp
                    : (typeof performance !== `undefined` ? performance.now() : Date.now());
                const progress = Math.min(1, (now - startTime) / duration);
                const easedProgress = 1 - Math.pow(1 - progress, 3);

                this.animatedGpsAccuracyMeters = currentMeters + ((targetMeters - currentMeters) * easedProgress);

                if (progress < 1) {
                    this.gpsAccuracyAnimationFrame = requestAnimationFrame(step);
                    return;
                }

                this.animatedGpsAccuracyMeters = targetMeters;
                this.gpsAccuracyAnimationFrame = null;
            };

            this.gpsAccuracyAnimationFrame = requestAnimationFrame(step);
        },
        stopGpsAccuracyRadiusAnimation() {
            if (this.gpsAccuracyAnimationFrame != null && typeof cancelAnimationFrame === `function`) {
                cancelAnimationFrame(this.gpsAccuracyAnimationFrame);
            }
            this.gpsAccuracyAnimationFrame = null;
        },
        startLiveCompass() {
            if (!this.useLiveCompass) {
                return;
            }

            this.isCompassActive = true;
            this.liveCompass.addEventListener(`compass`, this._compassHandler);
        },
        _compassHandler() {
            const heading = this.liveCompass.heading;
            const angle = Number.isFinite(heading) ? heading : 0;

            // Update live accuracy
            const accuracy = this.liveCompass.accuracy;
            this.liveAccuracy = Number.isFinite(accuracy) ? accuracy : 45;

            if (this.shape === `arrow` && this.$refs.arrowGroup) {
                this.$refs.arrowGroup.setAttribute(`transform`, `translate(0 0) rotate(${angle - 180}) scale(27)`);
            } else if (this.shape === `person` || this.shape === `dot`) {
                if (this.$refs.visionConeGroup) {
                    this.$refs.visionConeGroup.setAttribute(`transform`, `rotate(${angle})`);
                }
                if (this.$refs.personArrowGroup) {
                    this.$refs.personArrowGroup.setAttribute(`transform`, `rotate(${angle - 180})`);
                }
            }
        },
        stopLiveCompass() {
            if (this.isCompassActive && this.liveCompass) {
                this.liveCompass.removeEventListener(`compass`, this._compassHandler);
            }
            this.isCompassActive = false;
            this.liveAccuracy = null;
        }
    },
    watch: {
        gpsAccuracy: {
            immediate: true,
            handler(value) {
                this.animateGpsAccuracyRadius(value);
            }
        },
        liveCompass(newCompass, oldCompass) {
            if (oldCompass && this.isCompassActive) {
                oldCompass.removeEventListener(`compass`, this._compassHandler);
            }
            this.startLiveCompass();
        },
        shape() {
            // Shape change means different refs are available; rebind after DOM update
            this.$nextTick(() => {
                this.stopLiveCompass();
                this.startLiveCompass();
            });
        }
    },
}
</script>

<style lang="scss" scoped>
.map-location-marker {
    z-index: 10;
    transition: transform 500ms;

    * {
        transition: none;
    }

    .walking-person {
        border-radius: 9999px;
        object-fit: cover;
    }

    .binoculars-tooltip-bg {
        fill: #ffffff;
        // stroke: rgba(17, 24, 39, 0.14);
        stroke-width: 1.5px;
    }

    .binoculars-tooltip-circle {
        fill: white;
        stroke: color-mix(in srgb, var(--map-primary-color) 40%, white);
        stroke-width: 4px;
    }

    .binoculars-tooltip-icon {
        color: color-mix(in srgb, var(--map-primary-color) 60%, white);
    }

    .circle-around,
    .circle-marker {
        stroke: white;
        stroke-width: 2px;
        fill: var(--map-secondary-color);
    }

    .circle-around {
        opacity: 0.2;
    }

    .location-marker-bg {
        x: calc(-50px * var(--map-ui-scale));
        y: calc(34px * var(--map-ui-scale) + var(--map-label-size) * var(--map-line-height));
        width: calc(100px * var(--map-ui-scale));
        height: calc(24px * var(--map-ui-scale));
        fill: #eee;
    }

    .location-marker-label {
        transform: translateY(calc((34px + 24px / 2) * var(--map-ui-scale) + var(--map-label-size) * var(--map-line-height)));
        font-size: calc(1.2 * var(--map-label-size));
        font-weight: 700;
        fill: var(--map-secondary-color);
        user-select: none;
        alignment-baseline: central;
    }

    .circle-orientation,
    .arrow-marker {
        fill: var(--map-secondary-color);
    }

    .person-angle,
    .arrow-marker {
        fill: var(--map-secondary-color) !important;
        stroke: white;
        stroke-width: 0.08px;
        opacity: 1;
    }

    // Vision cone styling
    .vision-cone {
        pointer-events: none;
    }

    // Vision cone path - smooth transitions for accuracy changes
    .vision-cone-path {
        pointer-events: none;
        transition: d 0.5s ease-out;
    }

    // Blue dot styling (Google Maps style)
    .gps-accuracy-circle {
        fill: rgba(66, 133, 244, 0.2);
        stroke: none;
        pointer-events: none;
    }

    .blue-dot-inner {
        fill: url(#blue-dot-inner-gradient);
        stroke: white;
        stroke-width: 3px;
    }

    &.primary {

        .circle-around,
        .circle-marker,
        .circle-orientation,
        .arrow-marker,
        text {
            fill: var(--map-primary-color);
        }

        .gps-accuracy-circle {
            fill: rgba(var(--map-primary-color-rgb, 66, 133, 244), 0.2);
        }

        .blue-dot-inner {
            fill: url(#blue-dot-inner-gradient);
        }
    }

    &.outer-primary {
        .circle-around,
        .circle-marker,
        .circle-orientation,
        .arrow-marker,
        text {
            fill: var(--map-primary-color);
        }

        .gps-accuracy-circle {
            fill: rgba(var(--map-primary-color-rgb, 66, 133, 244), 0.2);
        }
    }

    &.gps {

        .circle-around,
        .circle-marker,
        .circle-orientation,
        .arrow-marker,
        text {
            fill: #ffffff;
        }

        .gps-accuracy-circle {
            fill: rgba(176, 52, 211, 0.2);
        }

        .blue-dot-inner {
            fill: url(#purple-dot-inner-gradient);
        }
    }

    &.gray {
        .circle-around,
        .circle-marker,
        .circle-orientation,
        .arrow-marker,
        text {
            fill: #888888;
        }

        .gps-accuracy-circle {
            fill: rgba(136, 136, 136, 0.2);
        }

        .blue-dot-inner {
            fill: url(#gray-dot-inner-gradient);
        }
    }

    &.different-floor {
        display: none;
        opacity: 0.5;
    }
}
</style>
